import Button from 'components/actions/Button';
import NoData from 'components/content/NoData';
import type { SortConfig, TableColumn } from 'components/table';
import { DataTable, getNextSortConfig, sortData } from 'components/table';
import type { VehicleSummary } from 'domain/vehicle';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemFleetsQuery } from 'services/systemAdmin/fleets';
import { useSummaryVehiclesV2Query } from 'services/vehiclesV2';
import type { RootState } from 'store/reducers';
import { getSelectedVehicleVin, setSelectedVehicleVin } from 'store/slices/settingsPageSlice';

import styles from './styles/SystemVehiclesTable.module.scss';
import { getVehicleTypeName, isVehicleStale } from './systemVehicleUtils';

const SystemVehiclesTable = () => {
    const { data, isLoading, isError, refetch } = useSummaryVehiclesV2Query();
    const vehicles = data ?? [];

    const { data: fleets = [] } = useListSystemFleetsQuery('ListFleets');
    const fleetNameById = useMemo(() => new Map(fleets.map(fleet => [fleet.id, fleet.name])), [fleets]);
    const getFleetName = useMemo(
        () => (fleetId?: string) => (fleetId ? (fleetNameById.get(fleetId) ?? 'Unassigned') : 'Unassigned'),
        [fleetNameById]
    );

    const columns = useMemo<TableColumn<VehicleSummary>[]>(
        () => [
            {
                key: 'name',
                title: 'Name',
                dataIndex: 'name',
                sortable: true,
            },
            {
                key: 'licensePlate',
                title: 'License Plate',
                dataIndex: 'licensePlate',
                sortable: true,
                render: value => (value ? String(value) : <NoData text='No data' className='text-size-14' />),
            },
            {
                key: 'vin',
                title: 'VIN',
                dataIndex: 'vin',
                sortable: true,
            },
            {
                key: 'type',
                title: 'Type',
                dataIndex: 'type',
                sortable: true,
                render: (_, record) => getVehicleTypeName(record),
                sortValue: getVehicleTypeName,
            },
            {
                key: 'fleetId',
                title: 'Assigned Fleet',
                dataIndex: 'fleetId',
                sortable: true,
                render: value =>
                    value ? (
                        getFleetName(value ? String(value) : undefined)
                    ) : (
                        <NoData text='Unassigned' className='text-size-14' />
                    ),
                sortValue: value => getFleetName(value.fleetId ? String(value.fleetId) : undefined),
            },
        ],
        [getFleetName, getVehicleTypeName]
    );

    const [sortConfig, setSortConfig] = useState<SortConfig<string>>({ key: 'name', dir: 'asc' });
    const dispatch = useDispatch();
    const selectedVin = useSelector((state: RootState) => getSelectedVehicleVin(state));

    const sortedVehicles = useMemo(() => sortData(vehicles, sortConfig, columns), [vehicles, sortConfig, columns]);

    return (
        <div className='display-flex flex-column' data-testid='system-vehicles-table-wrapper'>
            <DataTable
                data-testid='system-vehicles-table'
                columns={columns}
                dataSource={sortedVehicles}
                rowKey='vin'
                tableClassName=''
                stickyHeader
                rowClassName={record => (isVehicleStale(record) ? styles.StaleVehicleRow : undefined)}
                sortConfig={sortConfig}
                onSortChange={key => setSortConfig(prev => getNextSortConfig(prev, key))}
                onRowClick={record => dispatch(setSelectedVehicleVin(record.vin))}
                activeRowKey={selectedVin}
                actionColumn={{
                    ariaLabel: vehicle => `Actions for ${vehicle.name}`,
                    disabled: true,
                    actions: () => [
                        {
                            id: 'edit',
                            title: 'Edit',
                            icon: 'rioglyph-pencil',
                            onSelect: () => {},
                        },
                        {
                            id: 'delete',
                            title: 'Delete',
                            icon: 'rioglyph-trash',
                            variant: 'danger',
                            onSelect: () => {},
                        },
                    ],
                }}
                loadMore={{
                    loaded: vehicles.length,
                    total: vehicles.length,
                }}
                isLoading={isLoading}
                isError={isError}
                error={{
                    header: 'Failed to load vehicles',
                    message: 'If this issue persists, please contact the Control Center support team.',
                    onClick: () => {
                        refetch();
                    },
                }}
                empty={{
                    title: 'No vehicles yet, but they will appear here',
                    body: 'As of now, there are no vehicles in the Control Center.',
                    button: (
                        <Button data-testid='no-content-vehicles-button' style='primary' disabled>
                            <span className='rioglyph rioglyph-plus' />
                            Add new vehicle
                        </Button>
                    ),
                }}
            />
        </div>
    );
};

export default SystemVehiclesTable;
